import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { MoodysThemeProvider } from "@moodys/mdc-frontend.theming.mui-themes.theme";
import { sanitizeAndTransformHtml } from "@moodys/mdc-frontend.utils.html-sanitizer";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CachedIcon from "@mui/icons-material/Cached";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { Box, Button, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
export const is4xxError = (statusCode) => {
    return statusCode >= 400 && statusCode < 500;
};
export const ErrorPage = ({ correlationId, statusCode, contactUsEmail = "clientservices@moodys.com", moodysHomepage = `https://${window.location.host}`, serverErrorReloadButtonProps, hideServerErrorReloadButton, hideIncidentIdDisplay = false, }) => {
    let errorPageMessage, overlineMessage, errorDescriptiveMessage;
    const ErrorDescriptionBox = styled(Box)(({ theme }) => ({
        display: "inline-block",
        [theme.breakpoints.up("sm")]: {
            width: theme.typography.pxToRem(688),
        },
        [theme.breakpoints.up("md")]: {
            width: theme.typography.pxToRem(784),
        },
        [theme.breakpoints.up("lg")]: {
            width: theme.typography.pxToRem(992),
        },
    }));
    if (is4xxError(statusCode)) {
        errorPageMessage = "The page you’re looking for cannot be found.";
        overlineMessage = "404 ERROR";
        errorDescriptiveMessage = sanitizeAndTransformHtml({
            htmlString: `We're sorry, we can't find the page you're looking for.
        It might have been removed, changed its name, or is otherwise unavailable.
        If you believe you are seeing this message in error,
        please contact us at <a href='mailto:clientservices@moodys.com'>clientservices@moodys.com</a>`,
        });
    }
    else {
        errorPageMessage = "Something went wrong.";
        overlineMessage = "OOPS";
        if (serverErrorReloadButtonProps?.descriptiveMessage) {
            errorDescriptiveMessage = sanitizeAndTransformHtml({
                htmlString: serverErrorReloadButtonProps.descriptiveMessage,
            });
        }
    }
    const handleReloadPageClick = () => {
        window.location.reload();
    };
    let incident = "";
    if (correlationId && correlationId.trim().length > 0) {
        incident = ` Incident ID is ${correlationId}`;
    }
    const currentDateTime = new Date();
    const emailBody = `?subject=Error on ${window.location.host}` +
        `&body=Encountered an error while accessing ${window.location.origin + window.location.pathname} on ${currentDateTime}.${incident}`;
    return (_jsx(MoodysThemeProvider, { children: _jsxs(Stack, { alignContent: "center", "data-testid": "error-page-container", role: "alert", sx: { height: "100%", textAlign: "center" }, children: [_jsx(Box, { "data-testid": "error-page-overline-message", mb: 2, mt: 12, children: _jsx(Typography, { variant: "overline", color: "text.secondary", children: overlineMessage }) }), _jsxs(Box, { "data-testid": "error-page-message", pb: 10, children: [_jsx(Typography, { variant: "h1", children: errorPageMessage }), !hideIncidentIdDisplay &&
                            !is4xxError(statusCode) &&
                            correlationId && (_jsx(Box, { children: _jsxs(Typography, { "data-testid": "error-page-incident-id", variant: "caption", children: ["Incident ID: ", correlationId] }) })), errorDescriptiveMessage && (_jsx(ErrorDescriptionBox, { "data-testid": "error-page-descriptive-message", children: _jsx(Typography, { "data-testid": "error-page-descriptive-message-text", variant: "body1", children: errorDescriptiveMessage }) }))] }), is4xxError(statusCode) ? (_jsx(Box, { mb: 2, children: _jsx(Button, { "data-testid": "error-page-home-action", endIcon: _jsx(ArrowForwardIcon, { sx: { mb: "1px" } }), href: moodysHomepage, variant: "rounded", children: "Go to Moodys.com" }) })) : (!hideServerErrorReloadButton && (_jsx(Box, { mb: 2, children: _jsx(Button, { "data-testid": "error-page-reload-action", endIcon: !serverErrorReloadButtonProps?.startIcon && (_jsx(CachedIcon, { sx: { mb: "1px" } })), startIcon: serverErrorReloadButtonProps?.startIcon &&
                            serverErrorReloadButtonProps.startIcon, onClick: serverErrorReloadButtonProps?.onClick ?? handleReloadPageClick, variant: "rounded", children: serverErrorReloadButtonProps?.buttonText ?? "Reload Page" }) }))), _jsx(Box, { children: _jsx(Button, { "data-testid": "error-page-contact-us", endIcon: _jsx(MailOutlineIcon, { sx: { mb: "1px" } }), href: `mailto:${contactUsEmail}${emailBody}`, variant: "text", children: "Contact Us" }) })] }) }));
};
//# sourceMappingURL=error-page.js.map